import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const ProductionOutwardReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // -----------------------------
  // Helpers
  // -----------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && String(val).trim() !== ""
      ? val
      : fallback;
  };

  const toInt = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const toStr = (v, fallback = "") => {
    if (v === null || v === undefined) return fallback;
    const s = String(v).trim();
    return s !== "" ? s : fallback;
  };

  const normalizeDateForInput = (value, fallback = "") => {
    if (!value) return fallback;

    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) return value;

    const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value).trim());
    if (dmy) {
      const [, dd, mm, yyyy] = dmy;
      return `${yyyy}-${mm}-${dd}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return fallback;
    return d.toISOString().slice(0, 10);
  };

  const toDMY = (value) => {
    if (!value) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(String(value).trim())) return value;

    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
    if (iso) {
      const [, yyyy, mm, dd] = iso;
      return `${dd}/${mm}/${yyyy}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatDDMMYYYY = (val) => {
    if (!val) return "";
    if (typeof val === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;

    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val).slice(0, 10);

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  const formatNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
  };

  // -----------------------------
  // localStorage values
  // -----------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // -----------------------------
  // parsed values
  // -----------------------------
  const branchid = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchtype = useMemo(() => toStr(branchTypeLS, "HO"), [branchTypeLS]);
  const userid = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // -----------------------------
  // common headers
  // -----------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": toStr(finYearId, ""),
      "x-finyear": toStr(finYear, ""),
      "x-branchid": String(branchid || ""),
      "x-branchtype": branchtype,
      "x-userid": String(userid || ""),
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [projectOptions, setProjectOptions] = useState([]);
  const [productionOptions, setProductionOptions] = useState([]);
  const [outwardData, setOutwardData] = useState([]);

  const [formData, setFormData] = useState({
    fromDate: normalizeDateForInput("30/11/2025"),
    toDate: normalizeDateForInput("30/12/2025"),
    projectid: "",
    productionid: "All",
  });

  const totals = useMemo(() => {
    const totalTransfer = outwardData.reduce(
      (sum, r) =>
        sum +
        Number(
          r.TransferedQty ??
            r.Transferedqty ??
            r.transferedQty ??
            r.TransferredQty ??
            r.transferredQty ??
            0
        ),
      0
    );

    const totalBal = outwardData.reduce(
      (sum, r) => sum + Number(r.BalanceQty ?? r.balanceQty ?? 0),
      0
    );

    return { totalTransfer, totalBal };
  }, [outwardData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------
  // API: Projects dropdown
  // ---------------------------
  const loadProjects = async () => {
    if (!branchid) {
      setProjectOptions([]);
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/outwardListing/projects`, {
        params: { branchid },
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setProjectOptions(data);
    } catch (err) {
      console.error("Projects load error:", err);
      toast.error("Failed to load projects");
      setProjectOptions([]);
    }
  };

  // ---------------------------
  // API: Productions dropdown
  // ---------------------------
  const loadProductions = async (projectid) => {
    if (!branchid) {
      setProductionOptions([]);
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/outwardListing/productions`, {
        params: {
          branchid,
          projectid: Number(projectid || 0),
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setProductionOptions(data);
    } catch (err) {
      console.error("Productions load error:", err);
      toast.error("Failed to load productions");
      setProductionOptions([]);
    }
  };

  // ---------------------------
  // API: Listing
  // ---------------------------
  const loadListing = async () => {
    if (!branchid) {
      toast.error("Branch ID missing in localStorage");
      return;
    }

    if (!formData.fromDate || !formData.toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    try {
      setLoading(true);

      const params = {
        branchid,
        fromDate: toDMY(formData.fromDate),
        toDate: toDMY(formData.toDate),
        projectid: formData.projectid ? Number(formData.projectid) : null,
        productionid:
          formData.productionid === "All" || !formData.productionid
            ? null
            : Number(formData.productionid),
      };

      const res = await axios.get(`${BASE_URL}/api/outwardListing/list`, {
        params,
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setOutwardData(data);

      if (data.length === 0) toast.info("No Records found");
      else toast.success(`Found ${data.length} record(s)`);
    } catch (err) {
      console.error("Listing load error:", err);
      toast.error(err?.response?.data?.message || "Failed to fetch outward listing");
      setOutwardData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Initial load
  // ---------------------------
  useEffect(() => {
    loadProjects();
    loadProductions(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchid]);

  // ---------------------------
  // When project changes
  // ---------------------------
  useEffect(() => {
    setFormData((prev) => ({ ...prev, productionid: "All" }));
    loadProductions(formData.projectid || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectid]);

  const handleSearch = () => {
    loadListing();
  };

  // ---------------------------
  // Print handler
  // ---------------------------
  const handlePrint = (row) => {
    const outId =
      row.fld_ProdOutID ??
      row.prodOutId ??
      row.outwardId ??
      row.fld_outwardid ??
      row.id;

    if (!outId) {
      toast.error("Outward ID missing in this row");
      return;
    }

    window.open(
      `/TarMixing/Tar_Outward_Report_Print?outid=${outId}&branchid=${branchid}`,
      "_blank"
    );
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">PRODUCTION OUTWARD LISTING</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Outward Listing
            </h1>

            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Date
                    </label>
                    <input
                      type="date"
                      value={formData.fromDate}
                      onChange={(e) => handleInputChange("fromDate", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Project Name
                    </label>
                    <select
                      value={formData.projectid}
                      onChange={(e) => handleInputChange("projectid", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {projectOptions.map((p, idx) => {
                        const projectId =
                          p.id ??
                          p.projectid ??
                          p.projectId ??
                          p.fld_projectid ??
                          idx;

                        const projectName =
                          p.name ??
                          p.projectName ??
                          p.ProjectName ??
                          p.fld_ProjectName ??
                          "Unnamed Project";

                        return (
                          <option key={projectId} value={projectId}>
                            {projectName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To Date
                    </label>
                    <input
                      type="date"
                      value={formData.toDate}
                      onChange={(e) => handleInputChange("toDate", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Production Name
                    </label>
                    <select
                      value={formData.productionid}
                      onChange={(e) => handleInputChange("productionid", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="All">All</option>
                      {productionOptions.map((p, idx) => {
                        const productionId =
                          p.productionId ??
                          p.fld_productionid ??
                          p.id ??
                          p.ProductionId ??
                          idx;

                        const productionName =
                          p.productionName ??
                          p.fld_ProductionName ??
                          p.ProductionName ??
                          "Unnamed Production";

                        return (
                          <option key={productionId} value={productionId}>
                            {productionName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-center items-center mt-6 gap-4">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {outwardData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <div className={`overflow-x-auto ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <table className="min-w-full text-sm">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center">S.no</th>
                      <th className="px-4 py-3 text-center">Outward No</th>
                      <th className="px-4 py-3 text-center">Date</th>
                      <th className="px-4 py-3 text-center">Project Name</th>
                      <th className="px-4 py-3 text-center">Production Code</th>
                      <th className="px-4 py-3 text-center">Production Name</th>
                      <th className="px-4 py-3 text-center">Transfer Qty</th>
                      <th className="px-4 py-3 text-center">Bal Quantity</th>
                      <th className="px-4 py-3 text-center">Report</th>
                    </tr>
                  </thead>

                  <tbody>
                    {outwardData.map((row, idx) => {
                      const outwardNo = row.fld_outwardcode ?? row.outwardcode ?? "-";
                      const dateText = formatDDMMYYYY(row.fld_Date ?? row.date ?? "");
                      const projectName =
                        row.fld_ProjectName ?? row.projectName ?? row.ProjectName ?? "-";
                      const productionCode =
                        row.fld_ProductionCode ??
                        row.productionCode ??
                        row.ProductionCode ??
                        "-";
                      const productionName =
                        row.fld_ProductionName ??
                        row.productionName ??
                        row.ProductionName ??
                        "-";
                      const transferQty =
                        row.TransferedQty ??
                        row.transferredQty ??
                        row.Transferedqty ??
                        row.TransferredQty ??
                        0;
                      const balQty = row.BalanceQty ?? row.balanceQty ?? 0;

                      const rowKey =
                        row.fld_ProdOutID ??
                        row.prodOutId ??
                        row.outwardId ??
                        row.id ??
                        idx;

                      return (
                        <tr
                          key={rowKey}
                          className={`border-t ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          } ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-900/30"
                              : "bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-center">{idx + 1}</td>
                          <td className="px-4 py-3 text-center">{outwardNo}</td>
                          <td className="px-4 py-3 text-center">{dateText}</td>
                          <td className="px-4 py-3 text-center">{projectName}</td>
                          <td className="px-4 py-3 text-center">{productionCode}</td>
                          <td className="px-4 py-3 text-center">{productionName}</td>
                          <td className="px-4 py-3 text-center">{formatNumber(transferQty)}</td>
                          <td className="px-4 py-3 text-center">{formatNumber(balQty)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handlePrint(row)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="flex justify-end p-4">
                  <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    <div>TotalQty : {formatNumber(totals.totalBal)}</div>
                    <div>Total : {formatNumber(totals.totalTransfer)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionOutwardReport;